import type { Origin } from "@rally/ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { failureSaid } from "../../localization/failure.ts";
import { say } from "../../localization/say.ts";
import type { Answering } from "../../net/api.ts";
import { act, read } from "../../net/rally.ts";
import type { Me } from "../../session.ts";
import { hears } from "../../sound/sounding.ts";
import type { Attachment } from "../attachment/attaching.ts";
import { type Held, landed, opening } from "../held.ts";
import { shelfFor } from "../kept.ts";
import { type Line, type Row, rowsOf, type Wrote } from "../person/arrivals.ts";
import { type Candidate, namesIn, offeredIn } from "./mentions.ts";

type MyAgent = { account: string; handle: string; avatar: string | null };

export type Person = {
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  role?: string;
  agent?: boolean;
  model?: string | null;
};

const records = shelfFor<Line[]>();
const rosters = shelfFor<Person[]>();

export const readBefore = (conversation: string): boolean => records.has(conversation);

export type Saying = {
  showing: Held<Line[]>;

  lines: Line[];

  people: Person[];

  known: Map<string, Person>;

  rows: Row[];

  sentence: string | null;

  names: readonly Candidate[];

  offered: readonly Candidate[];

  again: () => void;

  whoIsHere: () => void;

  sayIt: (
    text: string,
    from: Origin | null,
    answering?: Answering,
    files?: readonly Attachment[],
    submission?: string,
    apps?: readonly string[],
  ) => Promise<boolean>;
};

export function useSaying(conversation: string, me: Me): Saying {
  const [record, setRecord] = useState<Held<Line[]>>(() => opening(conversation));
  const [roster, setRoster] = useState<Held<Person[]>>(() => opening(conversation));

  const [agents, setAgents] = useState<MyAgent[]>([]);

  const [wrote, setWrote] = useState<{ for: string; rows: Wrote[] }>({ for: conversation, rows: [] });

  const [told, setTold] = useState<{ for: string; text: string } | null>(null);

  const written = useRef(0);

  const again = useCallback(() => {
    void read<Line[]>("conversation.read", { conversation })
      .then((said) => {
        const mine: Line[] = said.filter(
          (one) => one.kind === "message" || one.kind === "stopped" || one.kind === "call",
        );

        records.keep(conversation, mine);
        setRecord((was) => landed(was, { for: conversation, state: "read", what: mine }));
      })
      .catch((cause: unknown) => {
        records.forget(conversation);
        rosters.forget(conversation);
        const why = failureSaid(cause, say("chat.broke"));
        setRecord((was) => landed(was, { for: conversation, state: "refused", why }));
      });
  }, [conversation]);

  useEffect(() => {
    void read<MyAgent[]>("my.agents")
      .then(setAgents)
      .catch(() => setAgents([]));
  }, []);

  const whoIsHere = useCallback(() => {
    void read<Person[]>("conversation.people", { conversation })
      .then((said) => {
        rosters.keep(conversation, said);
        setRoster((was) => landed(was, { for: conversation, state: "read", what: said }));
      })
      .catch(() => {
        setRoster((was) => landed(was, { for: conversation, state: "read", what: [] }));
      });
  }, [conversation]);

  useEffect(() => {
    setRecord(opening(conversation));
    setRoster(opening(conversation));
    setWrote({ for: conversation, rows: [] });
    setTold(null);
    again();
    whoIsHere();
  }, [conversation, whoIsHere, again]);

  const showing = records.drawn(record, conversation);
  const lines: Line[] = showing.state === "read" ? showing.what : [];
  const here = rosters.drawn(roster, conversation);
  const people: Person[] = here.state === "read" ? here.what : [];
  const mineToo = wrote.for === conversation ? wrote.rows : [];
  const sentence = told?.for === conversation ? told.text : null;
  const rows = showing.state === "read" ? rowsOf(lines, mineToo) : [];

  const known = new Map(people.map((one) => [one.account, one]));

  const stranger = lines.some((one) => one.by !== undefined && !known.has(one.by));
  useEffect(() => {
    if (stranger) whoIsHere();
  }, [stranger, whoIsHere]);

  if (!known.has(me.account)) {
    known.set(me.account, {
      account: me.account,
      handle: me.handle,
      displayName: me.displayName,
      avatar: me.avatar ?? null,
    });
  }

  async function sayIt(
    text: string,
    from: Origin | null,
    answering?: Answering,
    files: readonly Attachment[] = [],
    submission: string = crypto.randomUUID(),
    apps: readonly string[] = [],
  ): Promise<boolean> {
    written.current += 1;
    const key = `wrote-${written.current}`;
    const line: Wrote = {
      key,
      id: null,
      by: me.account,
      text,
      at: new Date().toISOString(),
      submission,
      from,
      answering: answering ?? null,
    };

    setWrote((was) => (was.for === conversation ? { for: conversation, rows: [...was.rows, line] } : was));
    try {
      const posted = await act<{ id: string; at: string; files: string[] }>("message.post", {
        conversation,
        text,
        submission,
        ...(answering ? { replyTo: { kind: answering.kind, id: answering.id } } : {}),
        ...(files.length > 0
          ? { files: files.map(({ key: fileKey, name, mediaType }) => ({ key: fileKey, name, mediaType })) }
          : {}),
        ...(apps.length > 0 ? { apps } : {}),
      });

      setWrote((was) =>
        was.for === conversation
          ? {
              for: conversation,
              rows: was.rows.map((one) => (one.key === key ? { ...one, id: posted.id, at: posted.at } : one)),
            }
          : was,
      );
      return true;
    } catch (cause) {
      setWrote((was) =>
        was.for === conversation
          ? { for: conversation, rows: was.rows.filter((one) => one.key !== key) }
          : was,
      );
      const why = failureSaid(cause, say("chat.broke"));
      setTold({ for: conversation, text: why });

      hears({ what: "send failed", conversation });
      setTimeout(() => setTold((was) => (was?.for === conversation ? null : was)), 4000);
      return false;
    }
  }

  const names = namesIn(people, agents);
  const offered = offeredIn(people, agents, me.account);

  return { showing, lines, people, known, rows, sentence, names, offered, again, whoIsHere, sayIt };
}
